import { Application, User } from '../types';
import { supabase } from '../utils/supabase';

export const fetchApplications = async (): Promise<Application[]> => {

    const applications = await supabase.from('applications').select('*');
    if (applications.error) {
        throw new Error(applications.error.message);
    }
    return applications.data || [];
}

export const insertApplication = async (application: Application): Promise<boolean> => {
    const { error } = await supabase.from('applications').insert([application]);
    if (error) {
        throw new Error(error.code);
    }
    return true;
}

export const updateApplication = async (id: string, application: Partial<Application>): Promise<boolean> => {
    const { error } = await supabase.from('applications').update(application).eq('id', id);
    if (error) {
        throw new Error(error.message);
    }
    return true;
}

export const deleteApplication = async (id: string): Promise<boolean> => {
    const { error } = await supabase.from('applications').delete().eq('id', id);
    if (error) {
        throw new Error(error.message);
    }
    return true;
}

export const uploadBusinessLogo = async (file: File): Promise<boolean> => {
    const { error } = await supabase.storage
        .from('business')
        .upload(`public/${file.name}`, file);

    if (error?.message === 'The resource already exists') {
        return true;
    }
    if (error) {
        throw new Error(error.message);
    }

    return true;
}

export const downloadBusinessLogo = async (fileName: string): Promise<Blob> => {
    const { data, error } = await supabase.storage
        .from('business')
        .download(`public/${fileName}`);

    if (error) {
        throw new Error(error.message);
    }

    if (!data) {
        throw new Error('No se pudo descargar el archivo.');
    }

    return data;
}

export const getBusinessLogoUrl = async (fileName: string): Promise<string> => {
    const { data } = supabase.storage
        .from('business')
        .getPublicUrl(`public/${fileName}`);

    if (!data) {
        throw new Error('No se pudo obtener la URL pública.');
    }

    return data.publicUrl || '';
}

export const uploadDocument = async (file: any, name: string): Promise<boolean> => {
    const { error } = await supabase.storage
        .from('documents')
        .upload(`public/${name}`, file, {
            contentType: "application/pdf"
        });

    if (error?.message === 'The resource already exists') {
        return true;
    }
    if (error) {
        throw new Error(error.message);
    }

    return true;
}

export const getDocumentUrl = async (fileName: string): Promise<string> => {
    const { data } = supabase.storage
        .from('documents')
        .getPublicUrl(`public/${fileName}`);

    if (!data) {
        throw new Error('No se pudo obtener la URL pública.');
    }

    return data.publicUrl || '';
}

export const getUsers = async (): Promise<User[]> => {
    const { data, error } = await supabase
        .from("users")
        .select("*")
        .order("id", { ascending: false });

    if (error) {
        throw new Error(error.message);
    }

    return data || [];
}

export const getUserData = async (email: string): Promise<User> => {
    const { data, error } = await supabase
        .from("users")
        .select("*")
        .eq("email", email)
        .single();

    if (error) {
        throw new Error(error.message);
    }

    return data as User;
}

export const insertUser = async (formData: { name: string; email: string; role: string; password: string; }): Promise<boolean> => {
    // 1. Crear usuario en autenticación
    const { error: authError } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
    });

    if (authError) {
        throw new Error(authError.message);
    }

    // 2. Agregar información adicional en la tabla users
    const { error: userError } = await supabase.from("users").insert({
        email: formData.email,
        name: formData.name,
        role: formData.role,
    });

    if (userError) {
        throw new Error(userError.message);
    }

    return true;
}

export const updateUser = async (userName: string, userRole: string, userEmail: string) => {
    const { error } = await supabase
        .from("users")
        .update({
            name: userName,
            role: userRole,
        })
        .eq("email", userEmail);

    if (error) throw error;
}

export const deleteUser = async (userEmail: string): Promise<boolean> => {
    const { error: userError } = await supabase
        .from("users")
        .delete()
        .eq("email", userEmail);

    if (userError) {
        throw new Error(userError.message);
    }

    return true;
}

export const getIsClosed = async (): Promise<boolean> => {
    const { data } = await supabase
        .from("settings")
        .select("*")
        .single();

    return data?.isClosed || false;
}

export const setIsClosed = async (isClosed: boolean): Promise<boolean> => {
    await supabase
        .from("settings")
        .update({ isClosed })
        .eq("id", 1);

    return true;
}
export const signIn = async (email: string, password: string): Promise<boolean> => {
    const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
    });

    if (error) {
        throw new Error(error.message);
    }

    return true;
}